import './LocalSort.scss';

import cn from 'classnames';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@/common/components/atoms/Button/Button';
import { ASC, DESC } from '@/common/constants';
import { useAuth } from '@/common/contexts/AuthContext';
import { ReactComponent as SortIcon } from '@/materiaux/assets/icons/arrow-light-bottom.svg';

import {
  LocalSortDefaultProps,
  LocalSortPropTypes,
} from './LocalSort.proptypes';

const LocalSort = ({
  className,
  disabled,
  isLoading,
  options,
  setSortCode,
  setSortDirection,
  sortCode,
  sortDirection,
  sortables,
}) => {
  const { t } = useTranslation();
  const { isShowNetPrice } = useAuth();
  /**
   * Concatenate options and sortables to create sorts list
   * @returns {array}
   */
  const sortItems = useMemo(
    () =>
      options.concat(
        Object.keys(sortables).reduce((acc, item, index) => {
          acc.push({
            ...sortables[item],
            code: `sortables.${item}.value`,
            key: `sortable-${index + 1}`,
          });
          return acc;
        }, []),
      ),
    [options, sortables],
  );

  /**
   * Return number of sortable elements
   * @returns {number}
   */
  const sortablesLength = useMemo(() => Object.keys(sortables).length, [
    sortables,
  ]);

  /**
   * Get sort code and update sortCode and sortDirection states with this value
   * @param {string} code : sort code
   */
  const handleSort = useCallback(
    (code) => {
      setSortCode(code);
      setSortDirection(sortDirection === ASC ? DESC : ASC);
    },
    [setSortCode, setSortDirection, sortDirection],
  );

  return (
    <ul
      className={cn(className, 'list-sort', {
        'list-sort--without-net-price': !isShowNetPrice,
        'list-sort--show-net-price': isShowNetPrice,
        [`list-sort--sortables-${sortablesLength}`]: sortablesLength > 0,
      })}
      data-qa-id="list-sort"
    >
      {sortItems.map((sort) => (
        <li
          className={cn('sort', `list-sort__${sort.key}`, {
            [`sort--${sortDirection}`]: sortCode === sort.code,
          })}
          key={`list-${sort.key}`}
        >
          <Button
            className="sort__button"
            onClick={() => handleSort(sort.code)}
            disabled={!sort.sortable || disabled || isLoading}
          >
            {sort.label_mobile ? (
              <>
                <span className="sort__label sort__label--desktop">
                  {t(sort.label)}
                </span>
                <span className="sort__label sort__label--mobile">
                  {t(sort.label_mobile)}
                </span>
              </>
            ) : (
              <span className="sort__label">{t(sort.label)}</span>
            )}
            {sort.sortable && <SortIcon className="sort__icon" />}
          </Button>
        </li>
      ))}
    </ul>
  );
};

LocalSort.defaultProps = {
  ...LocalSortDefaultProps,
};

LocalSort.propTypes = {
  ...LocalSortPropTypes,
};

export default LocalSort;
